import React, { useEffect, useMemo, useRef } from 'react';
import { Input, useColorMode } from '@chakra-ui/react';
import { format } from 'date-fns';

import { useCalendarSettings, RADIUS } from '../../../lib';
import { __ } from '../../../lib/i18n';

interface DateInputProps {
	inputType: string;
	inputValue: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	isDesktop?: boolean;
	minDate?: Date | null;
	maxDate?: Date | null;
}

export const DateInputField = ({ inputType, inputValue, onChange, isDesktop = false, minDate, maxDate }: DateInputProps) => {
	const { colorMode } = useColorMode();
	const { settings } = useCalendarSettings();
	const dateInputRef = useRef<HTMLInputElement | null>(null);

	const { calendarToolbarBackgroundColor, calendarTextColor, calendarBordersColor } = settings.styles[colorMode];
	const { toolbar: toolbarRadius } = settings.radius;

	const desktopStyles = useMemo(
		() => ({
			backgroundColor: `${calendarToolbarBackgroundColor} !important`,
			color: `${calendarTextColor} !important`,
			borderColor: `${calendarBordersColor || 'black'} !important`,
			maxW: '300px',
		}),
		[calendarToolbarBackgroundColor, calendarTextColor, calendarBordersColor]
	);

	const mobileStyles = useMemo(
		() => ({
			backgroundColor: `${calendarToolbarBackgroundColor} !important`,
			color: `${calendarTextColor} !important`,
			borderColor: `${calendarBordersColor} !important`,
			borderRadius: '0px !important',
			borderBottomLeftRadius: `${RADIUS[toolbarRadius]} !important`,
			py: '20px !important',
		}),
		[calendarToolbarBackgroundColor, calendarTextColor, calendarBordersColor, toolbarRadius]
	);

	const inputCss = useMemo(
		() => ({
			'&::-webkit-calendar-picker-indicator': {
				filter: `invert(${colorMode === 'light' ? 0 : 1})`,
			},
		}),
		[colorMode]
	);

	useEffect(() => {
		if (dateInputRef.current) {
			dateInputRef.current.value = inputValue;
		}
	}, [inputValue]);

	// Format min/max dates for the input element
	const minDateString = useMemo(() => {
		if (!minDate) return undefined;
		return inputType === 'month' ? format(minDate, 'yyyy-MM') : format(minDate, 'yyyy-MM-dd');
	}, [minDate, inputType]);

	const maxDateString = useMemo(() => {
		if (!maxDate) return undefined;
		return inputType === 'month' ? format(maxDate, 'yyyy-MM') : format(maxDate, 'yyyy-MM-dd');
	}, [maxDate, inputType]);

	return (
		<Input
			ref={dateInputRef}
			defaultValue={inputValue}
			onChange={onChange}
			type={inputType}
			placeholder={__('Select Date')}
			size='md'
			min={minDateString}
			max={maxDateString}
			{...(isDesktop ? desktopStyles : mobileStyles)}
			css={inputCss}
		/>
	);
};
